import React from 'react';
import { twMerge } from 'tailwind-merge';
import { applyTypography } from '../../utils/typoGraphy';
import { useMagnetic } from '../../hooks/use-magnetic';
import { useLang } from '../../i18n/LanguageContext';
import { ReactComponent as EditIcon16Filled } from '../../assets/icons/16px/icon_16px_edit_filled.svg';
import { ReactComponent as EditIcon20Filled } from '../../assets/icons/20px/icon_20px_edit_filled.svg';

/**
 * @example
 * <BaseButton type="primary" size="m" iconPosition="before">
 * TEXT
 * </BaseButton>
 *
 * type: primary / secondary / text / warning / criticalError
 * size: xl / l / m / s
 * iconPosition: before / after
 * option: icon isDisabled hideIcon isLoading={true}
 */

const SIZE_MAP = {
  xl: { height: 'h-[52px]', paddingText: 'py-[14px] px-4', paddingIcon: 'p-4' },
  l: { height: 'h-12', paddingText: 'py-3 px-4', paddingIcon: 'p-[14px]' },
  m: {
    height: 'h-[38px]',
    paddingText: 'py-[10px] px-3',
    paddingIcon: 'p-[10px]',
  },
  s: { height: 'h-[30px]', paddingText: 'py-[6px] px-3', paddingIcon: 'p-2' },
};

const MIN_WIDTH_MAP = {
  iconOnly: { xl: 'min-w-[52px]', l: 'min-w-12', m: 'min-w-9', s: 'min-w-6' },
  textOnly: {
    xl: 'min-w-[84px]',
    l: 'min-w-[84px]',
    m: 'min-w-[69px]',
    s: 'min-w-[57px]',
  },
  textAndIcon: {
    xl: 'min-w-[102px]',
    l: 'min-w-[98px]',
    m: 'min-w-[83px]',
    s: 'min-w-[80px]',
  },
};

const ICON_MAP = {
  xl: <EditIcon20Filled />,
  l: <EditIcon20Filled />,
  m: <EditIcon16Filled />,
  s: <EditIcon16Filled />,
};

const BUTTON_STYLE = {
  light: {
    primary: {
      default: 'bg-green-deep02 text-text-inverse border-green-deep02',
      hover:
        'hover:bg-green-deep04 hover:text-text-inverse hover:border-green-deep04',
      active:
        'active:bg-green-deep05 active:text-text-inverse active:border-green-deep05',
      disabled: 'bg-grayscale-gray03 text-text-inverse border-grayscale-gray03',
    },
    secondary: {
      default: 'bg-grayscale-gray02 text-text-primary border-none',
      hover:
        'hover:bg-grayscale-gray03 hover:text-text-primary hover:border-none',
      active:
        'active:bg-grayscale-gray04 active:text-text-primary active:border-none',
      disabled: 'bg-grayscale-gray02 text-grayscale-gray03 border-none',
    },
    text: {
      default: 'bg-transparent text-grayscale-gray07 border-transparent',
      hover: 'hover:text-green-deep03 hover:bg-transparent',
      active: 'active:bg-green-light01 active:text-green-deep03',
      disabled: 'bg-transparent text-grayscale-gray03 border-transparent',
    },
    text_basic: {
      default:
        'bg-grayscale-gray00 text-grayscale-gray07 border-grayscale-gray02',
      hover:
        'hover:text-grayscale-gray07 hover:bg-grayscale-gray01 hover:border-grayscale-gray02',
      active:
        'active:bg-grayscale-gray02 active:text-grayscale-gray09 active:border-grayscale-gray02',
      disabled:
        'bg-grayscale-gray00 text-grayscale-gray03 border-grayscale-gray02',
    },
    warning: {
      default:
        'bg-functional-failed03 text-text-inverse border-functional-failed03',
      hover:
        'hover:bg-functional-failed02 hover:text-text-inverse hover:border-functional-failed02',
      active:
        'active:bg-functional-failed02 active:text-text-inverse active:border-functional-failed02',
      disabled:
        'bg-functional-failed02 text-orange-300 border-functional-failed02',
    },
    criticalError: {
      default:
        'bg-functional-failed03 text-text-inverse border-functional-failed03',
      hover:
        'hover:bg-functional-failed02 hover:text-text-inverse hover:functional-failed02',
      active:
        'active:bg-functional-failed02 active:text-text-inverse active:border-functional-failed02',
      disabled: 'bg-grayscale-gray03 text-text-inverse border-grayscale-gray03',
    },
    notice: {
      default:
        'bg-functional-warning03 text-text-inverse border-functional-warning04',
      hover:
        'hover:bg-functional-warning02 hover:text-text-inverse hover:border-functional-warning02',
      active:
        'active:bg-functional-warning02 active:text-text-inverse active:border-functional-warning02',
      disabled: 'bg-grayscale-gray03 text-text-inverse border-grayscale-gray03',
    },
  },
  dark: {
    primary: {
      default: 'bg-dark-green-deep02 text-dark-text-inverse border-transparent',
      hover:
        'hover:bg-dark-green-deep04 hover:text-dark-text-inverse hover:border-transparent',
      active:
        'active:bg-dark-green-deep05 active:text-dark-text-inverse active:border-transparent',
      disabled:
        'bg-dark-grayscale-gray03 text-dark-text-disable border-transparent',
    },
    secondary: {
      default:
        'bg-dark-grayscale-gray02 text-dark-text-primary border-transparent',
      hover:
        'hover:bg-dark-grayscale-gray03 hover:text-dark-text-primary hover:border-transparent',
      active:
        'active:bg-dark-grayscale-gray04 active:text-dark-text-primary active:border-transparent',
      disabled:
        'bg-dark-grayscale-gray01 text-dark-text-disable border-transparent',
    },
    text: {
      default: 'bg-transparent text-dark-grayscale-gray07 border-transparent',
      hover:
        'hover:text-dark-green-deep03 hover:bg-transparent hover:border-transparent',
      active:
        'active:bg-dark-surface-green10 active:text-dark-green-deep04 active:border-transparent',
      disabled: 'bg-transparent text-dark-grayscale-gray04 border-transparent',
    },
    text_basic: {
      default:
        'bg-dark-grayscale-gray01 text-dark-grayscale-gray07 border-dark-grayscale-gray02',
      hover:
        'hover:text-dark-grayscale-gray07 hover:bg-dark-grayscale-gray02 hover:border-dark-grayscale-gray03',
      active:
        'active:bg-dark-grayscale-gray02 active:text-dark-grayscale-gray09 active:border-dark-grayscale-gray03',
      disabled:
        'bg-dark-grayscale-gray01 text-dark-grayscale-gray03 border-dark-grayscale-gray02',
    },
    warning: {
      default:
        'bg-dark-functional-failed03 text-dark-text-inverse border-transparent',
      hover:
        'hover:bg-dark-functional-failed02 hover:text-dark-text-inverse hover:border-transparent',
      active:
        'active:bg-dark-functional-failed02 active:text-dark-text-inverse active:border-transparent',
      disabled:
        'bg-dark-grayscale-gray03 text-dark-text-disable border-transparent',
    },
    criticalError: {
      default:
        'bg-dark-functional-failed03 text-dark-text-inverse border-transparent',
      hover:
        'hover:bg-dark-functional-failed02 hover:text-dark-text-inverse hover:border-transparent',
      active:
        'active:bg-dark-functional-failed02 active:text-dark-text-inverse active:border-transparent',
      disabled:
        'bg-dark-grayscale-gray03 text-dark-text-disable border-transparent',
    },
    notice: {
      default:
        'bg-dark-functional-warning03 text-dark-text-inverse border-transparent',
      hover:
        'hover:bg-dark-functional-warning02 hover:text-dark-text-inverse hover:border-transparent',
      active:
        'active:bg-dark-functional-warning02 active:text-dark-text-inverse active:border-transparent',
      disabled:
        'bg-dark-grayscale-gray03 text-dark-text-disable border-transparent',
    },
  },
};

const LoadingDots = () => (
  <span className='flex items-center gap-[3px]'>
    <span className='w-2 h-2 rounded-full bg-current animate-wave [animation-delay:0ms]' />
    <span className='w-2 h-2 rounded-full bg-current animate-wave [animation-delay:150ms]' />
    <span className='w-2 h-2 rounded-full bg-current animate-wave [animation-delay:300ms]' />
  </span>
);

const ROUNDED_CLASS = {
  false: 'rounded-lg',
  true: 'rounded-full',
  full: 'rounded-full',
  pill: 'rounded-[99px]',
  none: 'rounded-none',
};

const getRoundedClass = (rounded) => {
  if (rounded === true) return ROUNDED_CLASS.true;
  if (rounded === false) return ROUNDED_CLASS.false;
  return ROUNDED_CLASS[rounded] || ROUNDED_CLASS.false;
};

/**
 * BaseButton 컴포넌트
 *
 * @component
 * @param {object} props - 컴포넌트 속성
 * @param {React.ReactNode} props.children - 버튼 내부에 들어갈 텍스트 또는 내용
 * @param {"primary" | "secondary" | "text" | "warning" | "criticalError"} [props.type="primary"] - 버튼 유형
 * @param {"xl" | "l" | "m" | "s"} [props.size="m"] - 버튼의 크기.
 * @param {React.ReactNode} [props.icon=<EditIcon16Filled />] - 버튼에 표시할 아이콘 컴포넌트
 * @param {"before" | "after"} [props.iconPosition="before"] - 아이콘의 위치 (텍스트 앞/뒤)
 * @param {boolean} [props.isLoading=false] - 로딩 상태 여부
 * @param {boolean} [props.isDisabled=false] - 비활성화 여부
 * @param {boolean} [props.hideIcon=false] - 아이콘을 표시하지 않을지 여부
 * @param {"en" | "kr"} [props.lang="en"] - 언어별 타이포그래피 (kr: xl/l=KR Body default strong, m=KR Body small strong, s=KR Body B13 strong)
 * @param {"light" | "dark"} [props.mode="light"] - 라이트/다크 모드 색상 팔레트
 * @param {string | false} [props.typo] - 타이포그래피 키 오버라이드. false 를 주면 기본 typo 클래스를 붙이지 않는다(호출부에서 직접 지정할 때).
 * @param {"button" | "submit" | "reset"} [props.htmlType] - DOM button 의 type 속성. form 안에서 submit 되지 않게 하려면 'button' 을 준다.
 */
export default function BaseButton({
  children,
  as = 'button',
  type = 'primary',
  htmlType,
  size = 'm',
  lang,
  mode = 'light',
  typo,
  width,
  icon = ICON_MAP[size],
  iconAfter,
  iconPosition = 'before',
  isLoading = false,
  isDisabled = false,
  hideIcon = false,
  leftalign = false,
  separate = false,
  gap = 'gap-2',
  bgclass,
  borderclass,
  textclass,
  hoverclass,
  activeclass,
  disabledBgClass,
  disabledTextClass,
  disabledBorderClass,
  textWrapperClass,
  isStatic = false,
  magnetic = false,
  magneticStrength = 0.3,
  rounded = false,
  ...props
}) {
  const { lang: globalLang } = useLang();
  const effectiveLang = lang || globalLang || 'en';
  const wrapRef = useMagnetic(magnetic ? magneticStrength : 0);
  const innerRef = useMagnetic(magnetic ? magneticStrength * 0.4 : 0);
  const Component = as;
  const isButtonElement = Component === 'button';
  const {
    className,
    onClick,
    style: propStyle,
    tabIndex,
    ...restProps
  } = props;

  const effectiveMode = mode === 'dark' ? 'dark' : 'light';
  const style = (BUTTON_STYLE[effectiveMode] || BUTTON_STYLE.light)[type];
  const sizeConfig = SIZE_MAP[size];
  const roundedClass = getRoundedClass(rounded);

  const isIconOnly = !children && !hideIcon;
  const hasIcon = !hideIcon && icon;
  const hasIconAfter = !hideIcon && Boolean(iconAfter);

  const leadingIcon = hasIcon && iconPosition === 'before' ? icon : null;
  const trailingIcon = hasIconAfter
    ? iconAfter
    : hasIcon && iconPosition === 'after'
      ? icon
      : null;
  const hasAnyIcon = Boolean(leadingIcon || trailingIcon);

  const minWidth = width
    ? ''
    : MIN_WIDTH_MAP[
        isIconOnly ? 'iconOnly' : hasIcon ? 'textAndIcon' : 'textOnly'
      ][size];

  const padding = isIconOnly ? sizeConfig.paddingIcon : sizeConfig.paddingText;

  const TYPO_KEY = {
    en: { xl: 'b16Strong', l: 'b16Strong', m: 'b14Strong', s: 'b13Strong' },
    kr: {
      xl: 'defaultStrong', // KR/Body/default (strong)
      l: 'defaultStrong', // KR/Body/default (strong)
      m: 'smallStrong', // KR/Body/small (strong)
      s: 'b13Strong', // KR/Body/B13 (strong)
    },
  };
  // typo={false}: 호출부가 자체 typo-* 클래스를 쓰는 경우 기본 타이포그래피를 끈다
  // (typo-* 는 components 레이어라 twMerge 로 병합되지 않아 충돌하기 때문)
  const typographyClass =
    typo === false
      ? ''
      : applyTypography(
          typo || (TYPO_KEY[effectiveLang] || TYPO_KEY.en)[size],
          children,
          effectiveLang,
        );

  // m/s: 텍스트 요소 높이를 고정(20px/18px)하고 수직 센터 정렬
  // (Noto Sans KR 글리프가 하단으로 치우치는 문제 보정)
  const FIXED_TEXT_HEIGHT = { m: 'h-5', s: 'h-[18px]' };
  const fixedTextHeight = FIXED_TEXT_HEIGHT[size] || '';

  const finalClasses = twMerge(`
    inline-flex items-center justify-center relative 
    border ${roundedClass} rounded-full transition-all duration-200 whitespace-nowrap
    ${sizeConfig.height} ${padding} ${minWidth}
    ${
      isDisabled
        ? `${style.disabled} ${disabledBgClass || ''} ${
            disabledTextClass || ''
          } ${disabledBorderClass || ''}`
        : style.default
    }
    ${type === 'secondary' ? 'border-[1.6px]' : ''}
    ${
      isDisabled
        ? 'cursor-not-allowed'
        : isStatic
          ? 'cursor-default'
          : 'cursor-pointer'
    }
    ${isLoading ? 'pointer-events-none' : ''}
    ${
      isLoading || isDisabled || isStatic
        ? ''
        : `${style.hover} ${style.active} active:scale-[0.97]`
    }
    ${iconPosition === 'after' ? 'flex-row-reverse' : ''}
    ${`${bgclass || ''} ${borderclass || ''} ${textclass || ''} ${
      hoverclass || ''
    } ${activeclass || ''}`}
    ${typographyClass}
    ${className || ''}
  `);

  const handleClick = (event) => {
    if (!isButtonElement && (isDisabled || isLoading)) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  const buttonContent = (
    <Component
      {...restProps}
      {...(isButtonElement
        ? {
            disabled: isDisabled || isLoading,
            ...(htmlType ? { type: htmlType } : {}),
          }
        : {})}
      {...(!isButtonElement && (isDisabled || isLoading)
        ? { 'aria-disabled': true, tabIndex: -1 }
        : { tabIndex })}
      className={finalClasses}
      style={{ width, ...propStyle }}
      onClick={handleClick}
    >
      <span
        ref={magnetic ? innerRef : undefined}
        className={twMerge(
          `flex items-center w-full`,
          separate
            ? 'justify-between'
            : leftalign
              ? 'justify-start'
              : 'justify-center',
          hasAnyIcon && children ? gap : '',
          isLoading ? 'invisible' : '',
          magnetic ? 'magnetic' : '',
        )}
      >
        {leadingIcon}
        {children && (
          <span
            className={twMerge(
              'min-w-0 flex-1 text-center',
              leftalign ? 'flex-1 text-left' : '',
              fixedTextHeight
                ? `${fixedTextHeight} flex items-center ${
                    leftalign ? 'justify-start' : 'justify-center'
                  }`
                : hasAnyIcon
                  ? 'mt-0.5'
                  : '',
              textWrapperClass || '',
            )}
          >
            {children}
          </span>
        )}
        {trailingIcon && (
          <span className={hasIconAfter ? 'ml-auto' : ''}>{trailingIcon}</span>
        )}
      </span>

      {isLoading && (
        <span className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'>
          <LoadingDots />
        </span>
      )}
    </Component>
  );

  if (magnetic) {
    return (
      <span ref={wrapRef} className='inline-block magnetic'>
        {buttonContent}
      </span>
    );
  }

  return buttonContent;
}
